/** Service / contract reference for Honda maintenance (context + audit trail; not tax math). */

export type HondaServiceContract = {
  vendorName: string;
  programName: string;
  contractNumber: string;
  purchaseOrder: string;
  contractValidFrom: string;
  contractValidTo: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  customerNumber: string;
  documentDate: string;
  notes: string;
  currency: string;
  documentNumber: string;
  soldToBillTo: string;
  contactEmail: string;
  contactPhone: string;
};

export const HONDA_SERVICE_CONTRACT_STORAGE_KEY = "flights-honda-service-contract-v1";

export const DEFAULT_HONDA_SERVICE_CONTRACT: HondaServiceContract = {
  vendorName: "Honda Aircraft Company LLC",
  programName: "Model 420 P2 Program",
  contractNumber: "40003111",
  purchaseOrder: "42000026",
  contractValidFrom: "2025-02-28",
  contractValidTo: "2030-02-27",
  billingPeriodStart: "2026-02-01",
  billingPeriodEnd: "2026-02-28",
  customerNumber: "12825",
  documentDate: "2026-03-12",
  notes: "",
  currency: "USD",
  documentNumber: "90104407",
  soldToBillTo: "Utah Gas Corp",
  contactEmail: "flightready@haci.honda.com",
  contactPhone: "336-217-4707",
};

const KEYS = Object.keys(DEFAULT_HONDA_SERVICE_CONTRACT) as (keyof HondaServiceContract)[];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function mergeHondaServiceContract(raw: unknown): HondaServiceContract {
  const base = { ...DEFAULT_HONDA_SERVICE_CONTRACT };
  if (!isRecord(raw)) return base;
  for (const k of KEYS) {
    const v = raw[k];
    if (typeof v === "string") base[k] = v;
  }
  return base;
}

export function loadHondaServiceContractFromStorage(): HondaServiceContract {
  try {
    const t = localStorage.getItem(HONDA_SERVICE_CONTRACT_STORAGE_KEY);
    if (!t) return { ...DEFAULT_HONDA_SERVICE_CONTRACT };
    return mergeHondaServiceContract(JSON.parse(t) as unknown);
  } catch {
    return { ...DEFAULT_HONDA_SERVICE_CONTRACT };
  }
}

export function saveHondaServiceContractToStorage(c: HondaServiceContract): void {
  try {
    localStorage.setItem(HONDA_SERVICE_CONTRACT_STORAGE_KEY, JSON.stringify(c));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Input / textarea ids on the Maintenance tab (service reference form). */
export const HONDA_CONTRACT_DOM_IDS: Record<keyof HondaServiceContract, string> = {
  vendorName: "honda-c-vendor",
  programName: "honda-c-program",
  contractNumber: "honda-c-contract-no",
  purchaseOrder: "honda-c-po",
  contractValidFrom: "honda-c-valid-from",
  contractValidTo: "honda-c-valid-to",
  billingPeriodStart: "honda-c-bill-start",
  billingPeriodEnd: "honda-c-bill-end",
  customerNumber: "honda-c-customer",
  documentDate: "honda-c-doc-date",
  notes: "honda-c-notes",
  currency: "honda-c-currency",
  documentNumber: "honda-c-doc-num",
  soldToBillTo: "honda-c-sold-to",
  contactEmail: "honda-c-email",
  contactPhone: "honda-c-phone",
};

export function readHondaServiceContractFromForm(root: HTMLElement): HondaServiceContract {
  const base = { ...DEFAULT_HONDA_SERVICE_CONTRACT };
  for (const k of KEYS) {
    const id = HONDA_CONTRACT_DOM_IDS[k];
    const el = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(`#${id}`);
    if (el) base[k] = el.value;
  }
  return base;
}

export function applyHondaServiceContractToForm(root: HTMLElement, c: HondaServiceContract): void {
  for (const k of KEYS) {
    const id = HONDA_CONTRACT_DOM_IDS[k];
    const el = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(`#${id}`);
    if (el) el.value = c[k];
  }
}

/** Snapshot / JSON fragment (snake_case) for `honda_maintenance` in worksheet payload. */
export type HondaMaintenanceSnapshot = {
  monthly_program_usd: number;
  flight_hour_usd: number;
  landing_cycle_usd: number;
  service_contract: HondaServiceContract;
};

export function hondaMaintenanceFromForm(
  root: HTMLElement,
  monthlyUsd: number,
  hourUsd: number,
  landingUsd: number
): HondaMaintenanceSnapshot {
  return {
    monthly_program_usd: monthlyUsd,
    flight_hour_usd: hourUsd,
    landing_cycle_usd: landingUsd,
    service_contract: readHondaServiceContractFromForm(root),
  };
}

export function applyHondaMaintenanceSnapshot(root: HTMLElement, raw: unknown): void {
  if (!isRecord(raw)) return;
  const m = Number(raw.monthly_program_usd);
  const h = Number(raw.flight_hour_usd);
  const l = Number(raw.landing_cycle_usd);
  const moEl = root.querySelector<HTMLInputElement>("#honda-usd-monthly");
  const hEl = root.querySelector<HTMLInputElement>("#honda-usd-hour");
  const lEl = root.querySelector<HTMLInputElement>("#honda-usd-landing");
  if (moEl && Number.isFinite(m) && m >= 0) moEl.value = String(m);
  if (hEl && Number.isFinite(h) && h >= 0) hEl.value = String(h);
  if (lEl && Number.isFinite(l) && l >= 0) lEl.value = String(l);
  const merged = mergeHondaServiceContract(raw.service_contract);
  applyHondaServiceContractToForm(root, merged);
  saveHondaServiceContractToStorage(merged);
}
